/**
 * Application Logger Service
 * 
 * Centralized logging service that:
 * - Removes console statements in production
 * - Integrates with error tracking services
 * - Provides consistent logging format
 */

import * as Sentry from '@sentry/browser';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: any;
  timestamp: number;
  module?: string;
}

class AppLogger {
  private logLevel: LogLevel;
  private logBuffer: LogEntry[] = [];
  private readonly MAX_BUFFER_SIZE = 100;

  constructor() {
    // In production, only log warnings and errors
    this.logLevel = import.meta.env.PROD ? LogLevel.WARN : LogLevel.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = new Date(entry.timestamp).toISOString();
    const level = LogLevel[entry.level];
    const module = entry.module ? `[${entry.module}]` : '';
    return `${timestamp} ${level} ${module} ${entry.message}`;
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.MAX_BUFFER_SIZE) {
      this.logBuffer.shift();
    }
  }

  debug(message: string, context?: any, module?: string): void {
    const entry: LogEntry = { level: LogLevel.DEBUG, message, context, timestamp: Date.now(), module };
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage(entry), context || '');
    }
  }

  info(message: string, context?: any, module?: string): void {
    const entry: LogEntry = { level: LogLevel.INFO, message, context, timestamp: Date.now(), module };
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage(entry), context || '');
    }
  }

  warn(message: string, context?: any, module?: string): void {
    const entry: LogEntry = { level: LogLevel.WARN, message, context, timestamp: Date.now(), module };
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(entry), context || '');
    }

    // Send warnings to Sentry in production
    if (import.meta.env.PROD) {
      Sentry.captureMessage(message, 'warning');
    }
  }

  error(message: string, error?: Error | unknown, context?: any, module?: string): void {
    const entry: LogEntry = { 
      level: LogLevel.ERROR, 
      message, 
      context: { ...context, error: error instanceof Error ? error.message : error }, 
      timestamp: Date.now(), 
      module 
    };
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage(entry), error || '');
    }

    // Send errors to Sentry
    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: { ...context, module }
      });
    } else {
      Sentry.captureMessage(message, 'error');
    }
  }

  // Get recent logs for debugging
  getRecentLogs(): LogEntry[] {
    return [...this.logBuffer];
  }

  // Clear log buffer
  clearLogs(): void {
    this.logBuffer = [];
  }

  // Set log level dynamically
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }
}

// Export singleton instance
export const logger = new AppLogger();

// Export convenience functions
export const logDebug = (message: string, context?: any, module?: string) => 
  logger.debug(message, context, module);

export const logInfo = (message: string, context?: any, module?: string) => 
  logger.info(message, context, module);

export const logWarn = (message: string, context?: any, module?: string) => 
  logger.warn(message, context, module);

export const logError = (message: string, error?: Error | unknown, context?: any, module?: string) => 
  logger.error(message, error, context, module);